import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { AlertTriangle, Check, Clock, FileQuestion, Loader2, Minus, ShieldAlert, X } from 'lucide-react';
import { useCarrier } from '../../contexts/CarrierContext';
import { listReviews } from '../../lib/supervisionApi';
import { toFa } from '../../utils/faFormat';
import { jobTitle, WORK_FAMILIES } from '../../shared/installationProcess';

export const QUEUE_REASONS = {
  ai_retries_exhausted: 'هوش مصنوعی پاسخ نداد', skipped_cap: 'سقف دفعات هوش مصنوعی',
  human_only: 'بدون هوش مصنوعی', evidence_flag: 'هشدار در مدارک عکس',
  authenticity: 'اصالت عکس نیاز به بررسی دارد', audit_sample: 'نمونهٔ تصادفی کنترل',
  ai_error: 'خطای هوش مصنوعی', ai_deadline_exceeded: 'هوش مصنوعی در مهلت پاسخ نداد',
  ai_kickoff_failed: 'هوش مصنوعی اجرا نشد', installer_appeal: 'درخواست بررسی انسانی', reassignment: 'ارسال نیروی قبلی پس از تخصیص دوباره',
};
export function queueReason(reason) { return QUEUE_REASONS[reason?.split(':')[0]] || (reason ? 'نیاز به بررسی' : ''); }
export function duration(since, now = Date.now()) {
  const minutes = Math.max(0, Math.floor((now - Date.parse(since)) / 60000));
  if (!Number.isFinite(minutes)) return '۰ دقیقه';
  return toFa(minutes >= 60 ? `${Math.floor(minutes / 60)} ساعت و ${minutes % 60} دقیقه` : `${minutes} دقیقه`);
}
export function AiChip({ ai = {}, now = Date.now() }) {
  let Icon = Minus;
  let tone = 'neutral';
  let text = 'بدون نظر هوش مصنوعی';
  switch (ai.status) {
    case 'pending': Icon = Clock; tone = 'info'; text = 'در انتظار بررسی هوش مصنوعی'; break;
    case 'running': {
      Icon = Loader2; tone = 'info';
      const limit = Math.max(0, Math.round((Date.parse(ai.deadline_at) - Date.parse(ai.started_at)) / 60000));
      const elapsed = Math.max(0, Math.floor((now - Date.parse(ai.started_at)) / 60000));
      text = Number.isFinite(limit) && Number.isFinite(elapsed)
        ? `هوش مصنوعی در حال بررسی · ${toFa(elapsed)} از ${toFa(limit)} دقیقه` : 'هوش مصنوعی در حال بررسی';
      break;
    }
    case 'done':
      if (ai.recommendation === 'pass') { Icon = Check; tone = 'success'; text = 'پیشنهاد هوش مصنوعی: تأیید'; }
      else if (ai.recommendation === 'fail') { Icon = X; tone = 'danger'; text = `پیشنهاد هوش مصنوعی: رد · ${toFa(ai.finding_count ?? ai.violations?.length ?? 0)} ایراد`; }
      else if (ai.recommendation === 'needs_more_evidence') { Icon = FileQuestion; tone = 'warning'; text = 'پیشنهاد هوش مصنوعی: مدرک بیشتر'; }
      break;
    case 'error': Icon = ShieldAlert; text = 'بدون نظر هوش مصنوعی: خطا'; break;
    case 'timeout': Icon = Clock; text = 'بدون نظر هوش مصنوعی: مهلت تمام شد'; break;
    case 'skipped_cap': text = 'بدون هوش مصنوعی: سقف دفعات'; break;
    case 'not_applicable': text = 'فقط ناظر'; break;
    default: break;
  }
  return <span className={`sup-chip sup-${tone}`}><Icon size={16} aria-hidden="true" />{text}</span>;
}

export const DECISION_OUTCOMES = {
  passed: { Icon: Check, tone: 'success', text: 'تأیید شد' },
  failed: { Icon: X, tone: 'danger', text: 'رد شد' },
  skipped: { Icon: Minus, tone: 'neutral', text: 'کنار گذاشته شد' },
};
function DecisionChip({ decision }) {
  const { Icon, tone, text } = DECISION_OUTCOMES[decision.outcome] || { Icon: FileQuestion, tone: 'neutral', text: 'تصمیم مشخص نیست' };
  return <span className={`sup-chip sup-${tone}`}><Icon size={16} aria-hidden="true" />
    {decision.decided_kind === 'ai' ? `تصمیم خودکار (قاعدهٔ قبولی): ${text}` : text}</span>;
}

const VIEWS = { ready: 'آمادهٔ تصمیم', ai: 'در حال بررسی هوش مصنوعی', decided: 'تصمیم‌گرفته‌شده' };
const EMPTY = { ready: 'مرحله‌ای منتظر تصمیم شما نیست.', ai: 'هیچ ارسالی در حال بررسی هوش مصنوعی نیست.', decided: 'در هفت روز گذشته تصمیمی ثبت نشده است.' };

export default function ReviewQueue() {
  const { carrier } = useCarrier();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const view = Object.hasOwn(VIEWS, params.get('view')) ? params.get('view') : 'ready';
  const stageKey = params.get('stage_key') || '';
  const workFamily = Object.hasOwn(WORK_FAMILIES, params.get('work_family')) ? params.get('work_family') : '';
  const [data, setData] = useState(null);
  const [stages, setStages] = useState({});
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const request = useRef(null);
  const load = useCallback(async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    try {
      const result = await listReviews({ view, stageKey, workFamily, signal: controller.signal });
      if (controller.signal.aborted) return;
      setData(result); setError(''); setNow(Date.now());
      setStages((old) => ({ ...old, ...Object.fromEntries((result.items || []).map((item) => [item.stage.key, item.stage.label_fa])) }));
    } catch (e) { if (!controller.signal.aborted && e.name !== 'AbortError') setError(e.message); }
  }, [view, stageKey, workFamily]);
  useEffect(() => {
    setData(null); load();
    const refresh = () => { if (document.visibilityState !== 'hidden') load(); };
    const timer = setInterval(refresh, 30000);
    document.addEventListener('visibilitychange', refresh);
    return () => { request.current?.abort(); clearInterval(timer); document.removeEventListener('visibilitychange', refresh); };
  }, [load]);
  useEffect(() => {
    const old = document.title;
    document.title = `(${toFa(data?.counts?.ready || 0)}) صف بازبینی`;
    return () => { document.title = old; };
  }, [data?.counts?.ready]);
  const filter = (name, value) => {
    const next = new URLSearchParams(location.search);
    if (value) next.set(name, value); else next.delete(name);
    navigate({ pathname: '/supervisor', search: `?${next}` });
  };
  const reviewUrl = (item) => {
    const search = new URLSearchParams(location.search);
    if (view === 'decided' && item.attempt_no) search.set('attempt', item.attempt_no);
    else search.delete('attempt');
    return `/supervisor/review/${encodeURIComponent(item.job_stage_id)}?${search}`;
  };
  return <section className="sup" aria-labelledby="review-queue-title">
    <header className="sup-heading"><h2 id="review-queue-title" tabIndex={-1}>صف بازبینی مراحل</h2>
      <p>{carrier?.brand_name} · {view === 'decided' ? 'تصمیم‌های هفت روز گذشته' : 'قدیمی‌ترین ارسال اول'}</p></header>
    <div className="sup-filters">
      <div className="sup-views" role="group" aria-label="نمای صف">{Object.entries(VIEWS).map(([key, label]) =>
        <button type="button" key={key} aria-pressed={view === key} onClick={() => filter('view', key)}>{label} ({toFa(data?.counts?.[key] || 0)})</button>)}</div>
      <label>خانوادهٔ کاری<select value={workFamily} onChange={(e) => filter('work_family', e.target.value)}><option value="">همهٔ تخصص‌های مجاز</option>
        {Object.entries(WORK_FAMILIES).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
      <label>مرحله<select value={stageKey} onChange={(e) => filter('stage_key', e.target.value)}><option value="">همهٔ مراحل</option>
        {stageKey && !stages[stageKey] && <option value={stageKey}>مرحلهٔ انتخاب‌شده</option>}
        {Object.entries(stages).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
    </div>
    {error && <div className="sup-alert sup-danger" role="alert">{error} <button type="button" onClick={load}>دریافت دوباره</button></div>}
    {!data && !error && <p role="status">صف در حال دریافت است.</p>}
    {data && !error && !data.items?.length && <p className="sup-empty" role="status">{EMPTY[view]}</p>}
    {!error && <div className="sup-queue">{(data?.items || []).map((item) => {
      // A skip of a stage that had no pending attempt has nothing to open.
      const linked = item.decision?.outcome !== 'skipped' || !!item.attempt_no;
      const Row = linked ? Link : 'div';
      return <Row className="sup-row" key={`${item.job_stage_id}:${item.attempt_no ?? ''}:${item.decision?.outcome ?? ''}`} to={linked ? reviewUrl(item) : undefined}>
      <div><strong>{jobTitle(item.job)}</strong><p>مرحلهٔ {toFa(item.stage.ord)} از {toFa(item.stage.total)}: {item.stage.label_fa}
        {item.attempt_no > 1 && ` · ارسال ${toFa(item.attempt_no)}`}</p>
        <p className="sup-muted">{[item.job.address_short, item.installer_name, item.contractor_name].filter(Boolean).join(' · ')}</p>
        {item.review_mode === 'ai_only' && <span className="sup-chip sup-info"><ShieldAlert size={16} aria-hidden="true" />فقط هوش مصنوعی</span>}
        {item.due_at && <p className={Date.parse(item.due_at) < now && !item.decision ? 'sup-danger' : undefined}>مهلت بازبینی: {new Date(item.due_at).toLocaleString('fa-IR', { timeZone: 'Asia/Tehran' })}{Date.parse(item.due_at) < now && !item.decision ? ' · گذشته از مهلت' : ''}</p>}
        {item.queue_reason && <p>{queueReason(item.queue_reason)}</p>}</div>
      <div className="sup-row-state">{item.decision ? <><DecisionChip decision={item.decision} />
        {item.decision.decided_by_name && <span>توسط {item.decision.decided_by_name}</span>}
        {item.decision.against_ai && <span>برخلاف پیشنهاد هوش مصنوعی</span>}
        {item.decision.flagged && item.decision.outcome === 'passed' && <span className="sup-chip sup-warning"><AlertTriangle size={16} aria-hidden="true" />با وجود هشدار تأیید شد</span>}
        {item.decision.outcome === 'skipped' && item.decision.reason && <span dir="auto">{`دلیل: ${item.decision.reason}`}</span>}</> : <><AiChip ai={item.ai} now={now} /><span>{duration(item.ready_at || item.submitted_at, now)} در انتظار</span></>}</div>
    </Row>;
    })}</div>}
  </section>;
}
